import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAiUsage } from '../services/api';

const usd = (v) => `$${Number(v || 0).toFixed(Number(v) < 1 ? 4 : 2)}`;
const num = (v, lang) => Number(v || 0).toLocaleString(lang);

/**
 * What the AI actually consumed: tokens billed by the provider per call and
 * their estimated cost, for cost audit (Administration > AI).
 */
export default function AiUsagePanel({ refreshKey }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    adminAiUsage(30)
      .then((d) => { if (alive) { setData(d); setError(false); } })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [refreshKey]);

  const action = (a) => t(`admin.aiUsageAction_${a}`, { defaultValue: a });
  const when = (iso) => {
    try {
      return new Date(`${iso}`.endsWith('Z') ? iso : `${iso}Z`).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' });
    } catch { return iso; }
  };

  return (
    <div className="ai-usage">
      <h4 className="ai-usage-title">{t('admin.aiUsageTitle')}</h4>
      <p className="ai-usage-intro">{t('admin.aiUsageIntro')}</p>
      {error && <p className="ai-error">{t('admin.aiUsageError')}</p>}
      {data && (
        <>
          <div className="ai-usage-totals">
            {[['today', 'aiUsageToday'], ['last_7_days', 'aiUsage7d'], ['period', 'aiUsage30d']].map(([key, label]) => (
              <div key={key} className="ai-usage-total">
                <span className="ai-usage-total-label">{t(`admin.${label}`)}</span>
                <span className="ai-usage-total-cost">{usd(data[key].cost_usd)}</span>
                <span className="ai-usage-total-meta">
                  {t('admin.aiUsageCalls', { count: data[key].calls })}
                  {data[key].failed > 0 && ` · ${t('admin.aiUsageFailed', { count: data[key].failed })}`}
                  <br />
                  {t('admin.aiUsageTokens', {
                    input: num(data[key].input_tokens, i18n.language),
                    output: num(data[key].output_tokens, i18n.language),
                  })}
                </span>
              </div>
            ))}
          </div>

          {data.period.calls === 0 ? (
            <p className="ai-usage-empty">{t('admin.aiUsageEmpty')}</p>
          ) : (
            <>
              <div className="ai-usage-split">
                <table className="ai-usage-table">
                  <caption>{t('admin.aiUsageByAction')}</caption>
                  <tbody>
                    {data.by_action.map((r) => (
                      <tr key={r.action}><th scope="row">{action(r.action)}</th><td>{r.calls}</td><td>{usd(r.cost_usd)}</td></tr>
                    ))}
                  </tbody>
                </table>
                <table className="ai-usage-table">
                  <caption>{t('admin.aiUsageByModel')}</caption>
                  <tbody>
                    {data.by_model.map((r) => (
                      <tr key={r.model}><th scope="row"><code>{r.model}</code></th><td>{r.calls}</td><td>{usd(r.cost_usd)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <details className="ai-usage-recent">
                <summary>{t('admin.aiUsageRecent')}</summary>
                <div className="ai-usage-scroll">
                  <table className="ai-usage-table">
                    <thead>
                      <tr>
                        <th>{t('admin.aiUsageColDate')}</th>
                        <th>{t('admin.aiUsageColAction')}</th>
                        <th>{t('admin.aiUsageColModel')}</th>
                        <th>{t('admin.aiUsageColUser')}</th>
                        <th>{t('admin.aiUsageColIn')}</th>
                        <th>{t('admin.aiUsageColOut')}</th>
                        <th>{t('admin.aiUsageColCost')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent.map((r, i) => (
                        <tr key={i} className={r.ok ? '' : 'is-failed'}>
                          <td>{when(r.created_at)}</td>
                          <td>{action(r.action)}{!r.ok && ` (${r.error})`}</td>
                          <td><code>{r.model}</code></td>
                          <td>{r.user_email}</td>
                          <td>{num(r.input_tokens, i18n.language)}{r.images > 0 && ` + ${r.images} 📷`}</td>
                          <td>{num(r.output_tokens, i18n.language)}</td>
                          <td>{usd(r.cost_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </>
          )}
        </>
      )}
    </div>
  );
}
